import { useEffect, useMemo, useState } from 'react'
import { Card, cn } from '@trafiflow/shared-ui'
import {
  CheckCircle,
  Copy,
  Download,
  Loader2,
  Pencil,
  Save,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import type { CompanyVideoAsset } from './companyVideos.service'

function parseTags(value: string): string[] {
  return value
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
}

type TabId = 'tags' | 'details' | 'transcript'
type VideoVersion = 'original' | 'clean'

type Props = {
  video: CompanyVideoAsset
  downloadUrl?: string
  cleanVersionUrl?: string
  loading?: boolean
  error?: string | null
  saving?: boolean
  deleting?: boolean
  reanalyzing?: boolean
  onClose: () => void
  onSave: (input: {
    title: string
    description: string
    category: string
    tags: string[]
  }) => void | Promise<void>
  onDelete: () => void | Promise<void>
  onReanalyze?: () => void | Promise<void>
}

function SectionHeader({
  children,
  variant = 'blue',
}: {
  children: React.ReactNode
  variant?: 'blue' | 'red'
}) {
  return (
    <h4
      className={cn(
        'text-xs font-semibold uppercase tracking-wide mb-2',
        variant === 'blue' ? 'text-blue-600' : 'text-red-500',
      )}
    >
      {children}
    </h4>
  )
}

function MetadataRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <div className="flex items-start justify-between py-1.5">
      <span className="text-xs text-tf-text-muted">{label}</span>
      <span className="text-xs font-medium text-tf-text text-right max-w-[60%] break-words">
        {value}
      </span>
    </div>
  )
}

function formatLabel(val: string) {
  return val
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function CompanyVideoDetailModal({
  video,
  downloadUrl,
  cleanVersionUrl,
  loading = false,
  error,
  saving = false,
  deleting = false,
  reanalyzing = false,
  onClose,
  onSave,
  onDelete,
  onReanalyze,
}: Props) {
  const [activeTab, setActiveTab] = useState<TabId>('tags')
  const [activeVersion, setActiveVersion] = useState<VideoVersion>('original')
  const [editing, setEditing] = useState(false)
  const [copyFeedback, setCopyFeedback] = useState(false)

  // Edit form state
  const [editTitle, setEditTitle] = useState(video.title || '')
  const [editDescription, setEditDescription] = useState(video.description || '')
  const [editCategory, setEditCategory] = useState(video.category || '')
  const [editTags, setEditTags] = useState((video.tags || []).join(', '))

  useEffect(() => {
    setEditTitle(video.title || '')
    setEditDescription(video.description || '')
    setEditCategory(video.category || '')
    setEditTags((video.tags || []).join(', '))
  }, [video])

  const disableActions = saving || deleting || loading || reanalyzing

  const canPreview = useMemo(() => {
    return Boolean(downloadUrl) && video.status === 'ready'
  }, [downloadUrl, video.status])

  const activeVideoSrc =
    activeVersion === 'clean' && cleanVersionUrl ? cleanVersionUrl : downloadUrl

  const cleanReady = video.cleanStatus === 'ready' && Boolean(cleanVersionUrl)
  const cleanProcessing = video.cleanStatus === 'pending' || video.cleanStatus === 'processing'

  const aiReady = video.aiAnalysisStatus === 'ready'
  const aiProcessing = video.aiAnalysisStatus === 'processing'
  const aiPending = video.aiAnalysisStatus === 'pending'
  const aiFailed = video.aiAnalysisStatus === 'failed'
  // Allow re-trigger when: ready (re-run), failed (retry), pending (stuck), or no status yet
  const canReanalyze = !aiProcessing && video.status === 'ready'

  const handleCopyLink = async () => {
    if (!downloadUrl) return
    try {
      await navigator.clipboard.writeText(downloadUrl)
      setCopyFeedback(true)
      setTimeout(() => setCopyFeedback(false), 2000)
    } catch {
      /* clipboard not available */
    }
  }

  const handleSaveEdits = () => {
    onSave({
      title: editTitle.trim(),
      description: editDescription.trim(),
      category: editCategory.trim(),
      tags: parseTags(editTags),
    })
    setEditing(false)
  }

  const tabs: { id: TabId; label: string }[] = [
    { id: 'tags', label: 'Tags' },
    { id: 'details', label: 'Details' },
    { id: 'transcript', label: 'Transcript' },
  ]

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
          {/* Top bar: filename + actions */}
          <div className="px-5 py-3 border-b border-tf-border flex items-center justify-between gap-4 bg-tf-bg-muted">
            <div className="min-w-0">
              <p className="text-sm font-medium text-tf-text truncate">
                {video.title || video.fileName}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Original/Clean toggle */}
              {(cleanReady || cleanProcessing) && (
                <div className="flex items-center gap-2">
                  <div className="flex rounded border border-tf-border overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setActiveVersion('original')}
                      className={cn(
                        'px-2.5 py-1 text-xs font-medium transition-colors',
                        activeVersion === 'original'
                          ? 'bg-tf-text text-white'
                          : 'text-tf-text-muted hover:bg-tf-bg-hover',
                      )}
                    >
                      Original
                    </button>
                    <button
                      type="button"
                      onClick={() => cleanReady && setActiveVersion('clean')}
                      disabled={!cleanReady}
                      className={cn(
                        'px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-40',
                        activeVersion === 'clean'
                          ? 'bg-tf-text text-white'
                          : 'text-tf-text-muted hover:bg-tf-bg-hover',
                      )}
                    >
                      No Audio
                    </button>
                  </div>
                  {cleanProcessing && (
                    <span className="text-xs text-amber-600 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Processing...
                    </span>
                  )}
                </div>
              )}
              {downloadUrl && (
                <>
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-tf-border text-xs font-medium text-tf-text-sub hover:bg-tf-bg-hover transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    {copyFeedback ? 'Copied!' : 'Copy Link'}
                  </button>
                  <a
                    href={downloadUrl}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download
                  </a>
                </>
              )}
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded hover:bg-tf-bg-hover text-tf-text-muted"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body: video + right panel */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left: video preview */}
            <div className="flex-1 flex flex-col items-center justify-center bg-black p-4 min-w-0">
              {loading && (
                <div className="flex items-center gap-2 text-white/60">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">Loading...</span>
                </div>
              )}

              {!loading && canPreview && (
                <div className="w-full max-w-md">
                  <video
                    key={activeVersion}
                    controls
                    src={activeVideoSrc}
                    className="w-full rounded"
                  >
                    <track kind="captions" />
                  </video>
                </div>
              )}

              {!loading && !canPreview && (
                <div className="text-white/40 text-sm">
                  {video.status !== 'ready' ? 'Video not ready yet.' : 'No preview available.'}
                </div>
              )}

            </div>

            {/* Right: tab panel */}
            <div className="w-80 flex-shrink-0 border-l border-tf-border flex flex-col overflow-hidden">
              {/* Tabs */}
              <div className="flex border-b border-tf-border">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'flex-1 px-3 py-2.5 text-xs font-medium transition-colors text-center',
                      activeTab === tab.id
                        ? 'text-blue-600 border-b-2 border-blue-600'
                        : 'text-tf-text-muted hover:text-tf-text',
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {error && (
                  <div className="rounded border border-red-200 bg-tf-feedback-error-bg px-3 py-2">
                    <p className="text-xs text-red-700">{error}</p>
                  </div>
                )}

                {/* ===== TAGS TAB ===== */}
                {activeTab === 'tags' && (
                  <>
                    {/* Edit toggle */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-tf-text">Tags</span>
                      <button
                        type="button"
                        onClick={() => {
                          if (editing) {
                            handleSaveEdits()
                          } else {
                            setEditing(true)
                          }
                        }}
                        disabled={disableActions}
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50"
                      >
                        {editing ? (
                          <>
                            <Save className="w-3 h-3" />
                            Save
                          </>
                        ) : (
                          <>
                            <Pencil className="w-3 h-3" />
                            Edit
                          </>
                        )}
                      </button>
                    </div>

                    {/* Content Details */}
                    <div>
                      <SectionHeader>Content Details</SectionHeader>
                      {editing ? (
                        <div className="space-y-2">
                          <div>
                            <label className="text-[10px] text-tf-text-muted">Title</label>
                            <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              className="w-full px-2 py-1 border border-tf-border rounded text-xs text-tf-text focus:outline-none focus:ring-1 focus:ring-blue-500"
                              disabled={disableActions}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-tf-text-muted">Category</label>
                            <input
                              type="text"
                              value={editCategory}
                              onChange={(e) => setEditCategory(e.target.value)}
                              className="w-full px-2 py-1 border border-tf-border rounded text-xs text-tf-text focus:outline-none focus:ring-1 focus:ring-blue-500"
                              disabled={disableActions}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-tf-text-muted">
                              Tags (comma-separated)
                            </label>
                            <input
                              type="text"
                              value={editTags}
                              onChange={(e) => setEditTags(e.target.value)}
                              className="w-full px-2 py-1 border border-tf-border rounded text-xs text-tf-text focus:outline-none focus:ring-1 focus:ring-blue-500"
                              disabled={disableActions}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          <MetadataRow label="Brand" value={video.brandId || undefined} />
                          <MetadataRow label="Category" value={video.category || undefined} />
                          {video.tags && video.tags.length > 0 && (
                            <div className="py-1.5">
                              <span className="text-xs text-tf-text-muted">Tags</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {video.tags.map((t, i) => (
                                  <span
                                    key={i}
                                    className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100"
                                  >
                                    {t}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Visual Characteristics (AI data) */}
                    {aiReady && (
                      <div>
                        <SectionHeader>Visual Characteristics</SectionHeader>
                        <div className="space-y-0.5">
                          <MetadataRow
                            label="Action Type"
                            value={
                              video.contentRole
                                ? formatLabel(video.contentRole)
                                : undefined
                            }
                          />
                          <MetadataRow
                            label="Quality"
                            value={video.quality ? formatLabel(video.quality) : undefined}
                          />
                          <MetadataRow
                            label="Point of View"
                            value={
                              video.pointOfView ? formatLabel(video.pointOfView) : undefined
                            }
                          />
                          <MetadataRow
                            label="Shot Type"
                            value={video.shotSize ? formatLabel(video.shotSize) : undefined}
                          />
                        </div>
                      </div>
                    )}

                    {/* Voice */}
                    {aiReady && video.voice && (
                      <div>
                        <SectionHeader>Voice</SectionHeader>
                        <MetadataRow label="Voice" value={formatLabel(video.voice)} />
                      </div>
                    )}

                    {/* Additional Tags (semantic keywords) */}
                    {aiReady &&
                      video.semanticKeywords &&
                      video.semanticKeywords.length > 0 && (
                        <div>
                          <SectionHeader variant="red">Additional Tags</SectionHeader>
                          <div className="flex flex-wrap gap-1">
                            {video.semanticKeywords.map((kw, i) => (
                              <span
                                key={i}
                                className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-100"
                              >
                                {kw}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                    {/* AI status banners */}
                    {aiProcessing && (
                      <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 flex items-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 text-amber-600 animate-spin" />
                        <span className="text-xs text-amber-800">AI analysis in progress...</span>
                      </div>
                    )}
                    {aiPending && (
                      <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 flex items-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 text-amber-600 animate-spin" />
                        <span className="text-xs text-amber-800">Waiting for analysis to start...</span>
                      </div>
                    )}
                    {aiFailed && (
                      <div className="rounded border border-red-200 bg-red-50 px-3 py-2">
                        <span className="text-xs text-red-700">AI analysis failed.</span>
                      </div>
                    )}

                    {/* Re-analyze button */}
                    {canReanalyze && onReanalyze && (
                      <button
                        type="button"
                        onClick={onReanalyze}
                        disabled={disableActions}
                        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded border border-violet-200 text-xs font-medium text-violet-700 hover:bg-violet-50 transition-colors disabled:opacity-50"
                      >
                        {reanalyzing ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-3.5 h-3.5" />
                            {aiReady ? 'Re-analyze with AI' : aiFailed ? 'Retry AI Analysis' : aiPending ? 'Trigger Analysis' : 'Analyze with AI'}
                          </>
                        )}
                      </button>
                    )}
                  </>
                )}

                {/* ===== DETAILS TAB ===== */}
                {activeTab === 'details' && (
                  <>
                    <div>
                      <span className="text-sm font-semibold text-tf-text">Info</span>
                      {editing ? (
                        <div className="mt-2 space-y-2">
                          <div>
                            <label className="text-[10px] text-tf-text-muted">Description</label>
                            <textarea
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              className="w-full min-h-[80px] px-2 py-1 border border-tf-border rounded text-xs text-tf-text focus:outline-none focus:ring-1 focus:ring-blue-500"
                              disabled={disableActions}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div>
                      <p className="text-xs text-tf-text-muted mb-1">Name of the file</p>
                      <p className="text-xs font-medium text-tf-text break-all">
                        {video.fileName}
                      </p>
                    </div>

                    {/* Content Information */}
                    <div>
                      <SectionHeader>Content Information</SectionHeader>

                      {/* AI Description */}
                      {video.aiDescription && (
                        <div className="mb-3">
                          <p className="text-xs text-tf-text-muted mb-1">Description</p>
                          <p className="text-xs text-tf-text leading-relaxed">
                            {video.aiDescription}
                          </p>
                        </div>
                      )}

                      {/* Manual description fallback */}
                      {!video.aiDescription && video.description && (
                        <div className="mb-3">
                          <p className="text-xs text-tf-text-muted mb-1">Description</p>
                          <p className="text-xs text-tf-text leading-relaxed">
                            {video.description}
                          </p>
                        </div>
                      )}

                      {/* Action type detail */}
                      {video.actionType && (
                        <div className="mb-3">
                          <p className="text-xs text-tf-text-muted mb-1">Action</p>
                          <p className="text-xs text-tf-text">{video.actionType}</p>
                        </div>
                      )}

                      {/* Mood */}
                      {video.mood && (
                        <div className="mb-3">
                          <p className="text-xs text-tf-text-muted mb-1">Mood</p>
                          <p className="text-xs text-tf-text italic">{video.mood}</p>
                        </div>
                      )}

                      <div className="space-y-0.5">
                        {video.durationSec && video.durationSec > 0 && (
                          <MetadataRow
                            label="Duration"
                            value={`${video.durationSec.toFixed(1)}s`}
                          />
                        )}
                        {video.sizeBytes && video.sizeBytes > 0 && (
                          <MetadataRow
                            label="File Size"
                            value={formatBytes(video.sizeBytes)}
                          />
                        )}
                        {video.contentType && (
                          <MetadataRow label="Content Type" value={video.contentType} />
                        )}
                        <MetadataRow
                          label="Uploaded"
                          value={new Date(video.createdAt).toLocaleDateString()}
                        />
                        {video.analyzedAt && (
                          <MetadataRow
                            label="Analyzed"
                            value={new Date(video.analyzedAt).toLocaleString()}
                          />
                        )}
                      </div>
                    </div>

                    {/* Status row */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={cn(
                          'px-2 py-0.5 rounded text-[10px] font-medium',
                          video.status === 'ready'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-tf-bg-hover text-tf-text-sub',
                        )}
                      >
                        {video.status}
                      </span>
                      {video.cleanStatus === 'ready' && (
                        <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-2.5 h-2.5" />
                          Clean
                        </span>
                      )}
                      {aiReady && (
                        <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700">
                          <Sparkles className="w-2.5 h-2.5" />
                          AI Analyzed
                        </span>
                      )}
                    </div>
                  </>
                )}

                {/* ===== TRANSCRIPT TAB ===== */}
                {activeTab === 'transcript' && (
                  <div>
                    <span className="text-sm font-semibold text-tf-text">Transcript</span>
                    <div className="mt-3">
                      <SectionHeader>Transcription</SectionHeader>
                      <p className="text-xs text-tf-text-muted mb-1">Full Transcription</p>
                      <div className="rounded border border-tf-border bg-tf-bg-muted px-3 py-3">
                        {video.transcript ? (
                          <p className="text-xs text-tf-text whitespace-pre-wrap leading-relaxed">
                            {video.transcript}
                          </p>
                        ) : video.aiAnalysisStatus === 'processing' || video.aiAnalysisStatus === 'pending' ? (
                          <p className="text-xs text-tf-text-muted italic">
                            Transcript will be available once AI analysis completes.
                          </p>
                        ) : video.voice === 'silent' || video.voice === 'text_only' ? (
                          <p className="text-xs text-tf-text-muted italic">
                            No spoken audio detected in this video.
                          </p>
                        ) : (
                          <p className="text-xs text-tf-text-muted italic">
                            No transcription available
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Bottom actions */}
              <div className="px-4 py-3 border-t border-tf-border flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => onDelete()}
                  disabled={disableActions}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-red-200 text-xs font-medium text-red-700 hover:bg-tf-feedback-error-bg transition-colors disabled:opacity-50"
                >
                  {deleting ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Trash2 className="w-3 h-3" />
                  )}
                  Delete
                </button>
                {editing && (
                  <button
                    type="button"
                    onClick={handleSaveEdits}
                    disabled={disableActions || !editTitle.trim()}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Save className="w-3 h-3" />
                    )}
                    Save
                  </button>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </>
  )
}

function formatBytes(bytes?: number) {
  if (!bytes || bytes <= 0) return ''
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let idx = 0
  let val = bytes
  while (val >= 1024 && idx < units.length - 1) {
    val /= 1024
    idx++
  }
  return `${val.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`
}

export default CompanyVideoDetailModal
